import React, { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import useUser from 'common/hooks/useUser';
import Loading from 'core/Loading';
import { useDraftVenuesGroupedByMerchantId } from 'modules/venue-data-collection/DraftVenueListPage/useDraftVenues';

import Contracts from './banner-notifications/contracts/Contracts';
import DraftVenues from './banner-notifications/draft-venues/DraftVenues';
import UnverifiedMerchants from './banner-notifications/unverified-merchants/UnverifiedMerchants';
import { useOnboardingStatus } from './hooks/useOnboardingStatus';
import { useOpenKybProcesses } from './hooks/useOpenKybProcesses';
import { useUnverifiedMerchants } from './hooks/useUnverifiedMerchants';
import { useUserContracts } from './hooks/useUserContracts';

const FrontPageOnboardingContainer = styled.div`
  margin-block-end: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FrontPageOnboarding = ({ merchants, venues }: { merchants: Merchant[]; venues: Venue[] }) => {
  const { isEmployee } = useUser();
  const { openKybProcesses, isLoadingKybProccesses } = useOpenKybProcesses({ merchants });
  const merchantIds = merchants?.map(m => m.itemid.$oid);
  const { userContracts = [], isLoadingContracts } = useUserContracts();
  const { unverifiedMerchants } = useUnverifiedMerchants({ merchants });
  const { data: draftVenuesGroupedByMerchantId } = useDraftVenuesGroupedByMerchantId(merchantIds || [], [
    'BLANK',
    'READY_TO_SUBMIT',
  ]);
  const { onboardingStatusData, isFetchingOnboardingStatus } = useOnboardingStatus({ merchants });

  const {
    shouldRenderContracts,
    shouldRenderUnverifiedMerchants,
    shouldRenderDraftVenuesSetupList,
    shouldRenderNotifications,
  } = useMemo(() => {
    const shouldRenderContracts = userContracts.length > 0;
    const shouldRenderUnverifiedMerchants = unverifiedMerchants.length > 0;
    const shouldRenderDraftVenuesSetupList = draftVenuesGroupedByMerchantId.length > 0;
    return {
      shouldRenderContracts,
      shouldRenderUnverifiedMerchants,
      shouldRenderDraftVenuesSetupList,
      shouldRenderNotifications:
        shouldRenderContracts || shouldRenderUnverifiedMerchants || shouldRenderDraftVenuesSetupList,
    };
  }, [userContracts.length, unverifiedMerchants.length, draftVenuesGroupedByMerchantId.length]);

  const isLoading = isLoadingContracts || isLoadingKybProccesses || isFetchingOnboardingStatus;

  if (isLoading) {
    return <Loading />;
  }

  if (onboardingStatusData?.status === 'SSMO_ONBOARDING_PAGE') {
    return <Redirect to={'/onboarding-page'} />;
  }

  if (!merchants || !venues || !openKybProcesses) {
    return null;
  }

  if (merchants.length === 1 && openKybProcesses.length === 1) {
    return <Redirect to={`/merchant/${openKybProcesses[0].merchantId}/business-verification/intro`} />;
  }

  if (merchants.length === 1 && openKybProcesses.length === 1) {
    return <Redirect to={`/merchant/${openKybProcesses[0].merchantId}/business-verification/intro`} />;
  }

  const isSingleMerchantWithOnlyDraftVenues = merchants.length === 1 && draftVenuesGroupedByMerchantId.length === 1;
  // Redirect to venue data collection automatically to save the user clicking the draft venue data collection banner
  if (isSingleMerchantWithOnlyDraftVenues) {
    const [merchant] = draftVenuesGroupedByMerchantId;
    if (merchant.draftVenues.every(draftVenue => draftVenue.status !== 'VENUE_CREATED')) {
      return <Redirect to={`venue-data-collection/${merchant.merchantId}`} />;
    }
  }

  if (!isEmployee && !shouldRenderNotifications) {
    // Redirect to merchant view automatically to save the user clicking the merchant from the list
    const isSingleMerchantAndNoUnverifiedMerchants = unverifiedMerchants.length === 0 && merchants.length === 1;
    if (isSingleMerchantAndNoUnverifiedMerchants) {
      const merchantId = merchants[0].itemid.$oid;
      return <Redirect to={`/merchant/${merchantId}`} />;
    }

    // Redirect to venue data collection automatically to save the user clicking the draft venue data collection banner
    const isSingleVenueAndNoDraftVenues = draftVenuesGroupedByMerchantId.length === 0 && venues.length === 1;
    if (isSingleVenueAndNoDraftVenues) {
      const venueId = venues[0].slug;
      return <Redirect to={`/venue/${venueId}`} />;
    }
  }

  return shouldRenderNotifications ? (
    <FrontPageOnboardingContainer>
      {shouldRenderUnverifiedMerchants && <UnverifiedMerchants merchants={unverifiedMerchants} />}
      {shouldRenderDraftVenuesSetupList && (
        <DraftVenues draftVenuesGroupedByMerchant={draftVenuesGroupedByMerchantId} />
      )}
      {shouldRenderContracts && <Contracts userContracts={userContracts} />}
    </FrontPageOnboardingContainer>
  ) : null;
};
